<template>
  <div class='joined-circles'>
    <svg :id="`joined-circles-content-svg-${indexingKey}`" class='joined-circles__content' :width='width' :height='height' :label='label'
         xmlns='http://www.w3.org/2000/svg'
         xmlns:xlink='http://www.w3.org/1999/xlink'
    />
  </div>
</template>

<script>
import * as d3 from 'd3';
import _ from 'lodash';

export default {
  name: 'JoinedCirclesChart',
  props: {
    data: {
      type: Array,
      required: true,
    },
    label: {
      type: String,
      default: '',
    },
    width: {
      default: 700,
      type: Number,
    },
    height: {
      default: 65,
      type: Number,
    },
    indexingKey: {
      type: Number,
      required: true,
    },
    margin: {
      type: Object,
      required: false,
      default: () => ({
        top: 10,
        bottom: 10,
        left: 200,
        right: 300,
      }),
    },
  },
  data() {
    return {
    };
  },
  mounted() {
    this.buildChart();
  },
  computed: {
    xSum() {
      return d3.sum(this.data, d => d.fail) + d3.sum(this.data, d => d.pass) + d3.sum(this.data, d => d.info);
    },
    xFactor() {
      return (this.xSum !== 0) ? (this.width - this.margin.left - this.margin.right) / this.xSum : 0;
    },
    xBuffer() {
      const fail = d3.max(this.data, d => d.fail);
      const pass = d3.max(this.data, d => d.pass);
      const info = d3.max(this.data, d => d.info);
      return d3.max([fail, pass, info]);
    },
  },
  methods: {
    buildChart() {
      this.data.forEach(node => {
        // eslint-disable-next-line no-param-reassign
        node.uuid = _.uniqueId();
      });
      const svg = d3.select(`#joined-circles-content-svg-${this.indexingKey}`);
      // build normalization vectors
      const scaleSize = d3.scaleSqrt().domain([0, this.xBuffer]).range([10, d3.min([this.height - this.margin.top - this.margin.bottom, this.xBuffer])]);
      const scale = d3.scaleLinear().domain([0, this.xBuffer]).range([0, d3.min([this.width - this.margin.left - this.margin.right, this.xBuffer])]);
      const leftOffset = (this.width - this.margin.left - this.margin.right) > this.xBuffer ? this.xBuffer * this.xFactor : 0;
      svg.append('line')
        .style('stroke', '#9fa5b4')
        .style('stroke-width', 0.5)
        .attr('x1', 0)
        .attr('y1', (this.height) / 2)
        .attr('x2', this.width)
        .attr('y2', (this.height) / 2);
      const labelText = svg.append('text')
        .attr('x', 0)
        .attr('y', ((this.height) / 2) + 5)
        .attr('dx', 0)
        .attr('dy', 10)
        // eslint-disable-next-line no-unused-vars
        .attr('class', d => 'text-label');
      this.splitTextToRows(this.label).forEach((part, i) => {
        labelText.append('tspan')
          .text(part)
          .attr('x', 0)
          .attr('y', ((this.height) / 2) + 5)
          .attr('dx', 0)
          .attr('dy', 10 + i * 12);
      });
      svg.selectAll('circle')
        .data(this.data)
        .join(enter => {
          const g = enter.append('g');
          // Category tooltips
          g.append('text')
            .text(d => d.tooltip)
            .attr('x', 0)
            .attr('y', 10)
            // eslint-disable-next-line no-unused-vars
            .attr('id', d => `show-control-${this.indexingKey}-${d.uuid}`)
            // eslint-disable-next-line no-unused-vars
            .style('display', d => 'none')
            .attr('class', 'tooltip-label');
          // Category tooltip data
          const totalsGroup = g.append('g');
          // Info totals
          let currentGroup = totalsGroup.append('g');
          currentGroup.append('rect')
            // eslint-disable-next-line no-unused-vars
            .attr('x', d => 1)
            .attr('y', 15)
            .attr('rx', 3)
            .attr('width', 20)
            .attr('height', 12)
            // eslint-disable-next-line no-unused-vars
            .style('fill', d => '#A9B6C3')
            // eslint-disable-next-line no-unused-vars
            .style('fill-opacity', d => 0.1)
            // eslint-disable-next-line no-unused-vars
            .style('stroke', d => '#A9B6C3')
            // eslint-disable-next-line no-unused-vars
            .style('stroke-width', d => 1)
            .style('display', 'none')
            .attr('id', d => `show-control-info-rect-${this.indexingKey}-${d.uuid}`);
          currentGroup.append('text')
            .text(d => d.info)
          // eslint-disable-next-line no-unused-vars
            .attr('x', d => 1)
            .attr('y', 24)
            .attr('dx', 10)
            .attr('class', 'tooltip-info-data')
            .style('display', 'none')
            .attr('id', d => `show-control-info-rect-text-${this.indexingKey}-${d.uuid}`);
          // Pass totals
          currentGroup = totalsGroup.append('g');
          currentGroup.append('rect')
            // eslint-disable-next-line no-unused-vars
            .attr('x', d => 24)
            .attr('y', 15)
            .attr('rx', 3)
            .attr('width', 20)
            .attr('height', 12)
            // eslint-disable-next-line no-unused-vars
            .style('fill', d => '#00D0C4')
            // eslint-disable-next-line no-unused-vars
            .style('fill-opacity', d => 0.1)
            // eslint-disable-next-line no-unused-vars
            .style('stroke', d => '#00D0C4')
            // eslint-disable-next-line no-unused-vars
            .style('stroke-width', d => 1)
            .style('display', 'none')
            .attr('id', d => `show-control-pass-rect-${this.indexingKey}-${d.uuid}`);
          currentGroup.append('text')
            .text(d => d.pass)
            // eslint-disable-next-line no-unused-vars
            .attr('x', d => 1)
            .attr('y', 24)
            .attr('dx', 33)
            .attr('class', 'tooltip-pass-data')
            .style('display', 'none')
            .attr('id', d => `show-control-pass-rect-text-${this.indexingKey}-${d.uuid}`);
          // Fail totals
          currentGroup = totalsGroup.append('g');
          currentGroup.append('rect')
            // eslint-disable-next-line no-unused-vars
            .attr('x', d => 48)
            .attr('y', 15)
            .attr('rx', 3)
            .attr('width', 20)
            .attr('height', 12)
            // eslint-disable-next-line no-unused-vars
            .style('fill', d => '#DF399F')
            // eslint-disable-next-line no-unused-vars
            .style('fill-opacity', d => 0.1)
            // eslint-disable-next-line no-unused-vars
            .style('stroke', d => '#DF399F')
            // eslint-disable-next-line no-unused-vars
            .style('stroke-width', d => 1)
            .style('display', 'none')
            .attr('id', d => `show-control-fail-rect-${this.indexingKey}-${d.uuid}`);
          currentGroup.append('text')
            .text(d => d.fail)
            // eslint-disable-next-line no-unused-vars
            .attr('x', d => 1)
            .attr('y', 24)
            .attr('dx', 57)
            .attr('class', 'tooltip-fail-data')
            .style('display', 'none')
            .attr('id', d => `show-control-fail-rect-text-${this.indexingKey}-${d.uuid}`);
          // INFO
          g.append('circle')
            .attr('cx', (d, i) => {
              const spacing = (this.width - this.margin.right - scale(this.xBuffer) * scale(this.xFactor)) / (this.data.length);
              return (scale(this.xBuffer) / 2) + (i * spacing) + this.margin.left + leftOffset + this.getRandomOffset(-15, 15);
            })
            .attr('cy', (this.height / 2) + this.getRandomOffset(-5, 5))
            // eslint-disable-next-line no-unused-vars
            .attr('r', (d, i) => (scaleSize(d.info)) / 2)
            .attr('tooltip', d => d.tooltip)
            .attr('value', d => d.info)
            // eslint-disable-next-line no-unused-vars
            .style('fill', d => '#A9B6C3')
            // eslint-disable-next-line no-unused-vars
            .style('fill-opacity', d => 0.8);
          // PASS
          g.append('circle')
            .attr('cx', (d, i) => {
              const spacing = (this.width - this.margin.right - scale(this.xBuffer) * scale(this.xFactor)) / (this.data.length);
              return (scale(this.xBuffer) / 2) + (i * spacing) + this.margin.left + leftOffset + this.getRandomOffset(-15, 15);
            })
            .attr('cy', (this.height / 2) + this.getRandomOffset(-10, 10))
          // eslint-disable-next-line no-unused-vars
            .attr('r', (d, i) => (scaleSize(d.pass)) / 2)
            .attr('tooltip', d => d.tooltip)
            .attr('value', d => d.pass)
          // eslint-disable-next-line no-unused-vars
            .style('fill', d => '#00D0C4')
          // eslint-disable-next-line no-unused-vars
            .style('fill-opacity', d => 0.8);
          // FAIL
          g.append('circle')
            .attr('cx', (d, i) => {
              const spacing = (this.width - this.margin.right - scale(this.xBuffer) * scale(this.xFactor)) / (this.data.length);
              return (scale(this.xBuffer) / 2) + (i * spacing) + this.margin.left + leftOffset + this.getRandomOffset(-5, 5);
            })
            .attr('cy', this.height / 2)
            // eslint-disable-next-line no-unused-vars
            .attr('r', (d, i) => (scaleSize(d.fail)) / 2)
            .attr('tooltip', d => d.tooltip)
            .attr('value', d => d.fail)
            // eslint-disable-next-line no-unused-vars
            .style('fill', d => '#DF399F')
            // eslint-disable-next-line no-unused-vars
            .style('fill-opacity', d => 0.8);
          g.append('rect')
            .attr('x', (d, i) => {
              const spacing = (this.width - this.margin.right - scale(this.xBuffer) * scale(this.xFactor)) / (this.data.length);
              return (scale(this.xBuffer) / 2) + (i * spacing) + this.margin.left + leftOffset - 25;
            })
            .attr('y', 0)
            .attr('width', 50)
            .attr('height', this.height)
            .style('fill-opacity', 0)
            .on('mouseover', (e, d) => {
              d3.select(`#show-control-${this.indexingKey}-${d.uuid}`)
                .style('display', 'block');
              d3.select(`#show-control-info-rect-${this.indexingKey}-${d.uuid}`)
                .style('display', 'block');
              d3.select(`#show-control-pass-rect-${this.indexingKey}-${d.uuid}`)
                .style('display', 'block');
              d3.select(`#show-control-fail-rect-${this.indexingKey}-${d.uuid}`)
                .style('display', 'block');
              d3.select(`#show-control-info-rect-text-${this.indexingKey}-${d.uuid}`)
                .style('display', 'block');
              d3.select(`#show-control-pass-rect-text-${this.indexingKey}-${d.uuid}`)
                .style('display', 'block');
              d3.select(`#show-control-fail-rect-text-${this.indexingKey}-${d.uuid}`)
                .style('display', 'block');
            })
            .on('mouseout', (e, d) => {
              d3.select(`#show-control-${this.indexingKey}-${d.uuid}`)
                .style('display', 'none');
              d3.select(`#show-control-info-rect-${this.indexingKey}-${d.uuid}`)
                .style('display', 'none');
              d3.select(`#show-control-pass-rect-${this.indexingKey}-${d.uuid}`)
                .style('display', 'none');
              d3.select(`#show-control-fail-rect-${this.indexingKey}-${d.uuid}`)
                .style('display', 'none');
              d3.select(`#show-control-info-rect-text-${this.indexingKey}-${d.uuid}`)
                .style('display', 'none');
              d3.select(`#show-control-pass-rect-text-${this.indexingKey}-${d.uuid}`)
                .style('display', 'none');
              d3.select(`#show-control-fail-rect-text-${this.indexingKey}-${d.uuid}`)
                .style('display', 'none');
            });
          return g;
        });
    },
    getRandomOffset(min, max) {
      return Math.floor(Math.random() * (Math.floor(max - Math.ceil(min + 1) + Math.ceil(min)))); // The maximum is inclusive and the minimum is inclusive
    },
    splitTextToRows(text) {
      const words = text.split(' ');
      const result = [];
      let row = [];
      for (let i = 0; i < words.length; i += 1) {
        row.push(words[i]);
        if (row.length === 3) {
          result.push(row.join(' '));
          row = [];
        }
      }
      if (row.length > 0) {
        result.push(row.join(' '));
      }
      return result;
    },
  },
};
</script>
<style scoped lang="scss">
@import '~@/assets/styles/design-system';

.joined-circles {
}
</style>

<style lang="scss">
@import '~@/assets/styles/design-system';

.joined-circles {
  &__content {
    .text-label {
      fill: var(--text-primary);
      font-family: $font-family-medium;
      text-transform: uppercase;
      font-size: $font-size-xs;
      letter-spacing: 1.1px;
    }

    .tooltip-label {
      font-family: $default-application-old-font-regular;
      font-size: rem(9);
      fill: var(--text-secondary);
      text-transform: uppercase;
    }

    .tooltip-info-data {
      fill: var(--text-secondary);
      font-family: $font-family-medium;
      font-size: $font-size-2xs;
      text-anchor: middle;
    }

    .tooltip-pass-data {
      fill: var(--severity-low);
      font-family: $font-family-medium;
      font-size: $font-size-2xs;
      text-anchor: middle;
    }

    .tooltip-fail-data {
      fill: var(--severity-critical);
      font-family: $font-family-medium;
      font-size: $font-size-2xs;
      text-anchor: middle;
    }
  }
}
</style>
